import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Heart, Plus, Trash2, Edit, Save, X, Upload, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLizCouplesPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading } = useAuth();
  const [couples, setCouples] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    couple_no: "", couple_name: "", photo_url: "", photo_position: "center", announced_date: "", male_name: "", female_name: "",
    male_location: "", female_location: "", male_birth_year: "", female_birth_year: "", message: "",
    male_story: "", female_story: "",
  });

  useEffect(() => {
    if (!loading && (!isAuthenticated || user?.role !== "admin")) navigate("/");
  }, [isAuthenticated, user, loading, navigate]);

  useEffect(() => { if (token) fetchCouples(); }, [token]);

  const fetchCouples = async () => {
    try {
      const res = await fetch(`${API_URL}/api/liz-couples`);
      if (res.ok) { const d = await res.json(); setCouples(d.couples || []); }
    } catch {} finally { setIsLoading(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", "image");
      const res = await fetch(`${API_URL}/api/upload`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, photo_url: data.url }));
      }
    } catch {} finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleSubmit = async () => {
    if (!form.male_name?.trim() || !form.female_name?.trim()) {
      toast.error("남성/여성 이름을 모두 입력해주세요.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        couple_no: form.couple_no ? parseInt(form.couple_no) : undefined,
        male_birth_year: form.male_birth_year ? parseInt(form.male_birth_year) : null,
        female_birth_year: form.female_birth_year ? parseInt(form.female_birth_year) : null,
      };
      const url = editingId ? `${API_URL}/api/admin/liz-couples/${editingId}` : `${API_URL}/api/admin/liz-couples`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editingId ? "커플 정보를 수정했습니다." : "새 커플이 등록되었습니다.");
        resetForm();
        fetchCouples();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "등록에 실패했습니다. 다시 시도해주세요.");
      }
    } catch (err) {
      toast.error("네트워크 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (couple) => {
    setForm({
      couple_no: couple.couple_no?.toString() || "",
      couple_name: couple.couple_name || "",
      photo_url: couple.photo_url || "",
      photo_position: couple.photo_position || "center",
      announced_date: couple.announced_date || "",
      male_name: couple.male_name || "",
      female_name: couple.female_name || "",
      male_location: couple.male_location || "",
      female_location: couple.female_location || "",
      male_birth_year: couple.male_birth_year?.toString() || "",
      female_birth_year: couple.female_birth_year?.toString() || "",
      message: couple.message || "",
      male_story: couple.male_story || "",
      female_story: couple.female_story || "",
    });
    setEditingId(couple.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    await fetch(`${API_URL}/api/admin/liz-couples/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    fetchCouples();
  };

  const handleToggleVisibility = async (couple) => {
    await fetch(`${API_URL}/api/admin/liz-couples/${couple.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_visible: !couple.is_visible }),
    });
    fetchCouples();
  };

  const resetForm = () => {
    setForm({ couple_no: "", couple_name: "", photo_url: "", photo_position: "center", announced_date: "", male_name: "", female_name: "", male_location: "", female_location: "", male_birth_year: "", female_birth_year: "", message: "", male_story: "", female_story: "" });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-liz-couples-page">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin")} className="p-2 hover:bg-stone-200 rounded-lg"><ArrowLeft className="w-5 h-5 text-stone-600" /></button>
            <div>
              <h1 className="text-2xl font-bold text-stone-800">리즈커플 관리</h1>
              <p className="text-sm text-stone-500">커플 게시판 사이드바에 표시될 커플 정보</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> 커플 등록
          </Button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-stone-800">{editingId ? "커플 수정" : "새 커플 등록"}</h2>
              <button onClick={resetForm} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Photo */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">커플 사진</label>
                <div className="flex items-center gap-4">
                  {form.photo_url ? (
                    <img
                      src={form.photo_url}
                      alt="커플"
                      className="w-20 h-20 rounded-xl object-cover border-2 border-pink-200"
                      style={{ objectPosition: form.photo_position === "top" ? "center top" : form.photo_position === "bottom" ? "center bottom" : "center" }}
                      data-testid="couple-photo-preview"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-stone-100 border-2 border-dashed border-stone-300 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-stone-300" />
                    </div>
                  )}
                  <div>
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg text-sm font-medium text-stone-600 transition-colors disabled:opacity-50"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploading ? "업로드중..." : "사진 업로드"}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                    <p className="text-xs text-stone-400 mt-1">정사각형 사진 권장</p>
                  </div>
                </div>
                {/* 사진 촛점(표시) 위치 */}
                {form.photo_url && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-stone-500 mb-1.5">사진 정렬 (잘릴 때 보여줄 위치)</p>
                    <div className="inline-flex rounded-lg border border-stone-200 overflow-hidden">
                      {[{ v: "top", l: "위" }, { v: "center", l: "가운데" }, { v: "bottom", l: "아래" }].map((o) => (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setForm(p => ({ ...p, photo_position: o.v }))}
                          className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                            (form.photo_position || "center") === o.v ? "bg-pink-500 text-white" : "bg-white text-stone-600 hover:bg-stone-50"
                          }`}
                          data-testid={`couple-photo-pos-${o.v}`}
                        >
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">호수</label>
                <input type="number" value={form.couple_no} onChange={(e) => setForm(p => ({ ...p, couple_no: e.target.value }))}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-burgundy-300" placeholder="자동 부여" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">커플명</label>
                <input type="text" value={form.couple_name} onChange={(e) => setForm(p => ({ ...p, couple_name: e.target.value }))}
                  data-testid="admin-couple-name-input"
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-burgundy-300" placeholder="예: 봄봄커플" />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">커플 발표일</label>
                <input type="date" value={form.announced_date} onChange={(e) => setForm(p => ({ ...p, announced_date: e.target.value }))}
                  data-testid="admin-couple-date-input"
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-burgundy-300" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-stone-500 mb-1">한마디 (선택)</label>
                <input type="text" value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-burgundy-300" placeholder="축하 메시지 / 커플 소개 한 줄" />
              </div>

              {/* Male */}
              <div className="bg-blue-50 rounded-xl p-4 space-y-2">
                <p className="text-xs font-bold text-blue-600">남성</p>
                <input type="text" value={form.male_name} onChange={(e) => setForm(p => ({ ...p, male_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm" placeholder="성 (예: 김)" />
                <input type="text" value={form.male_location} onChange={(e) => setForm(p => ({ ...p, male_location: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm" placeholder="지역 (예: 서울)" />
                <input type="number" value={form.male_birth_year} onChange={(e) => setForm(p => ({ ...p, male_birth_year: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm" placeholder="출생년도 (예: 1976)" />
                <textarea value={form.male_story} onChange={(e) => setForm(p => ({ ...p, male_story: e.target.value }))}
                  data-testid="admin-male-story-input" rows={5}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm resize-y" placeholder="남성의 이야기 (어떻게 만났는지, 마음이 어땠는지 등)" />
              </div>

              {/* Female */}
              <div className="bg-pink-50 rounded-xl p-4 space-y-2">
                <p className="text-xs font-bold text-pink-600">여성</p>
                <input type="text" value={form.female_name} onChange={(e) => setForm(p => ({ ...p, female_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm" placeholder="성 (예: 이)" />
                <input type="text" value={form.female_location} onChange={(e) => setForm(p => ({ ...p, female_location: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm" placeholder="지역 (예: 부산)" />
                <input type="number" value={form.female_birth_year} onChange={(e) => setForm(p => ({ ...p, female_birth_year: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm" placeholder="출생년도 (예: 1973)" />
                <textarea value={form.female_story} onChange={(e) => setForm(p => ({ ...p, female_story: e.target.value }))}
                  data-testid="admin-female-story-input" rows={5}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm resize-y" placeholder="여성의 이야기 (어떻게 만났는지, 마음이 어땠는지 등)" />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={resetForm}>취소</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="btn-primary flex items-center gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? "수정" : "등록"}
              </Button>
            </div>
          </div>
        )}

        {/* Couples List */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-100 flex items-center justify-between">
            <h2 className="font-bold text-stone-800">등록된 커플 ({couples.length})</h2>
          </div>
          {isLoading ? (
            <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400 mx-auto" /></div>
          ) : couples.length === 0 ? (
            <div className="p-12 text-center text-stone-400">등록된 커플이 없습니다. 위의 "커플 등록" 버튼을 눌러 추가하세요.</div>
          ) : (
            <div className="divide-y divide-stone-100">
              {couples.map((c) => {
                const mYear = c.male_birth_year ? String(c.male_birth_year).slice(2) : "?";
                const fYear = c.female_birth_year ? String(c.female_birth_year).slice(2) : "?";
                return (
                  <div key={c.id} className={`flex items-center gap-4 px-4 py-3 hover:bg-stone-50 ${!c.is_visible ? "opacity-50" : ""}`}>
                    {/* Photo */}
                    {c.photo_url ? (
                      <img src={c.photo_url} alt={`${c.couple_no}호`} className="w-14 h-14 rounded-xl object-cover border-2 border-pink-200 flex-shrink-0" style={{ objectPosition: c.photo_position === "top" ? "center top" : c.photo_position === "bottom" ? "center bottom" : "center" }} />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-stone-100 border-2 border-dashed border-stone-300 flex items-center justify-center flex-shrink-0">
                        <Heart className="w-5 h-5 text-stone-300" />
                      </div>
                    )}
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-burgundy-600">{c.couple_no}호 커플</span>
                        {!c.is_visible && <span className="text-[10px] bg-stone-200 text-stone-500 px-1.5 py-0.5 rounded">비공개</span>}
                      </div>
                      <p className="text-sm text-stone-600">
                        {c.male_name}OO ({c.male_location}, {mYear}) &middot; {c.female_name}OO ({c.female_location}, {fYear})
                      </p>
                      {c.message && <p className="text-xs text-stone-400 mt-0.5">{c.message}</p>}
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handleToggleVisibility(c)} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" title={c.is_visible ? "숨기기" : "보이기"}>
                        {c.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(c)} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-blue-500">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
